/**
 * Calculate the relative luminance of a color
 * @param {string} color - Hex color string (e.g., '#ffffff')
 * @returns {number} Relative luminance value between 0 and 1
 */
const getLuminance = color => {
  // Remove # if present
  const hex = color.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  // Apply gamma correction
  const [rs, gs, bs] = [r, g, b].map(c =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4,
  );

  // Calculate luminance
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Calculate contrast ratio between two colors
 * @param {string} color1 - First color (hex string)
 * @param {string} color2 - Second color (hex string)
 * @returns {number} Contrast ratio between 1 and 21
 */
export const getContrastRatio = (color1, color2) => {
  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);

  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);

  return (brightest + 0.05) / (darkest + 0.05);
};

/**
 * Get optimal text color (soft dark or white) for given background color
 * @param {string} backgroundColor - Background color (hex string)
 * @returns {string} Optimal text color (#242727 or #ffffff)
 */
export const getContrastColor = backgroundColor => {
  const darkColor = '#242727'; // Softer dark gray instead of pure black
  const whiteContrast = getContrastRatio(backgroundColor, '#ffffff');
  const darkContrast = getContrastRatio(backgroundColor, darkColor);

  // Return color with higher contrast ratio
  return whiteContrast > darkContrast ? '#ffffff' : darkColor;
};

/**
 * Check if a color meets WCAG contrast requirements
 * @param {string} textColor - Text color (hex string)
 * @param {string} backgroundColor - Background color (hex string)
 * @param {string} level - WCAG level ('AA' or 'AAA')
 * @returns {boolean} Whether the contrast meets the requirement
 */
export const meetsContrastRequirement = (textColor, backgroundColor, level = 'AA') => {
  const contrast = getContrastRatio(textColor, backgroundColor);
  const requirement = level === 'AAA' ? 7 : 4.5;
  return contrast >= requirement;
};

export default getContrastColor;
